import React from "react"
import { FormattedMessage } from "react-intl"
import { Link as RouterLink } from "react-router-dom"
import {
  useTheme,
  InfoIcon,
  Typography,
  Box,
  IconButton,
  Dialog,
  CloseIcon,
  UnorderedList,
  UnorderedListItem,
  Button,
  Link
} from "@carrotfertility/carotene-core"
import { formatMemberId } from "#/utils/formatMemberId"
import { useQuery } from "@tanstack/react-query"
import { carrotClient } from "#/utils/CarrotClient"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { Paths } from "#/utils/Paths"

export function MemberIdDialog() {
  const theme = useTheme()
  const { data: memberId, isLoading } = useQuery({
    queryKey: ["memberId"],
    queryFn: () => carrotClient.getMemberId(),
    retry: buildCarrotApiRetryPolicy(1),
    refetchOnWindowFocus: false
  })
  const [isMemberIdDialogOpen, setIsMemberIdDialogOpen] = React.useState(false)
  const formattedMemberId = !isLoading ? formatMemberId(memberId) : undefined
  return (
    <>
      <Box padding={theme.spacing(theme.tokens.spacing.xl, theme.tokens.spacing.md, theme.tokens.spacing.xs)}>
        <Typography variant="h6">
          <FormattedMessage defaultMessage="Member ID" />
        </Typography>
        <Box display="flex" alignItems="center">
          <Link variant="body2" color="textSecondary" component={RouterLink} to={Paths.MEMBER_ID}>
            {formattedMemberId}
          </Link>
          <IconButton
            size="medium"
            onClick={() => setIsMemberIdDialogOpen(true)}
            sx={{ padding: theme.spacing(theme.tokens.spacing.xs) }}
          >
            <InfoIcon fontSize="medium" />
          </IconButton>
        </Box>
      </Box>
      <Dialog open={isMemberIdDialogOpen} onClose={() => setIsMemberIdDialogOpen(false)}>
        <Box padding={theme.spacing(theme.tokens.spacing.md)}>
          <Box display="flex" justifyContent="flex-end">
            <IconButton onClick={() => setIsMemberIdDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography variant="h3" marginBottom={theme.spacing(theme.tokens.spacing.xs)}>
            <FormattedMessage defaultMessage="What's a Carrot Member ID?" />
          </Typography>

          <Box>
            <UnorderedList>
              <UnorderedListItem>
                <Typography>
                  <FormattedMessage
                    defaultMessage="When you <link>call us</link>, you can use your member ID to identify yourself and get faster service."
                    values={{
                      link: (linkContent) => (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                        <Link
                          component={RouterLink}
                          color="inherit"
                          to="/talk-to-carrot"
                          onClick={() => setIsMemberIdDialogOpen(false)}
                        >
                          {linkContent}
                        </Link>
                      )
                    }}
                  />
                </Typography>
              </UnorderedListItem>
              <UnorderedListItem>
                <Typography>
                  <FormattedMessage
                    defaultMessage="You can also use your ID to <link>access savings</link> and other benefits at partner providers."
                    values={{
                      link: (linkContent) => (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                        <Link
                          component={RouterLink}
                          color="inherit"
                          to={Paths.MEMBER_ID}
                          onClick={() => setIsMemberIdDialogOpen(false)}
                        >
                          {linkContent}
                        </Link>
                      )
                    }}
                  />
                </Typography>
              </UnorderedListItem>
              <UnorderedListItem>
                <Typography>
                  <FormattedMessage defaultMessage="You don't need your ID to seek care at non-partner providers." />
                </Typography>
              </UnorderedListItem>
            </UnorderedList>
          </Box>
          <Box display="flex" justifyContent="flex-end">
            <Button onClick={() => setIsMemberIdDialogOpen(false)}>
              <FormattedMessage defaultMessage="Close" />
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  )
}
